import { Button, Modal, Switch, Table, Typography } from "antd";
import { type HTMLAttributes, useState } from "react";
import { useIntl } from "react-intl";
import { AddPersonRolesModal } from "~components/AddPersonRolesModal";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { RoleStatuses } from "~enums/roleStatuses";
import type { Roles } from "~enums/roles";
import { useDeleteRole } from "~hooks/apiHooks/useDeleteRole";
import { useFetchRoles } from "~hooks/apiHooks/useFetchRoles";
import { useUpdateRole } from "~hooks/apiHooks/useUpdateRole";
import { useHandlePagination } from "~hooks/useHandlePagination";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { onTableChange } from "~utilities/onTableChange";
import { transformRoleResponse } from "~utilities/transformRoleResponse";
import classNames from "~utils/classNames";
import { SeasonOfficialsFilters } from "./components/SeasonOfficialsFilters";
import { useOfficialsColumns } from "./hooks/useOfficialsColumns";
import styles from "./SeasonOfficialsPage.module.css";
import type { SeasonOfficialsTableRow } from "./types/seasonOfficialsTableRow";

type TableCellProps = HTMLAttributes<HTMLTableCellElement> & {
  isEditing: boolean;
  record: SeasonOfficialsTableRow;
};

const TableCell = ({ isEditing, record, ...props }: TableCellProps) => (
  <td {...props} className={classNames(props.className, isEditing ? styles.editCell : styles.cell)} />
);

const roleStatuses = [RoleStatuses.Active, RoleStatuses.Pending, RoleStatuses.Inactive];

const DEFAULT_SORT_FIELD = "role";
const SeasonOfficialsPage = () => {
  const { formatMessage } = useIntl();

  const { seasonOfficialRoles } = useSportConfiguration();
  const [filteredRoles, setFilteredRoles] = useState<Roles[]>([]);
  const [filteredStatus, setFilteredStatus] = useState<RoleStatuses | undefined>();
  const [isMassEdit, setIsMassEdit] = useState(false);
  const [isAddOfficialModalOpen, setIsAddOfficialModalOpen] = useState(false);

  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(50);

  const { mutateAsync: updateRole, isPending: isUpdating } = useUpdateRole({
    onSuccess: () => refetch(),
  });
  const { mutateAsync: deleteRole, isPending: isDeleting } = useDeleteRole({
    onSuccess: () => refetch(),
  });
  const [modal, contextHolder] = Modal.useModal();
  const columns = useOfficialsColumns(
    seasonOfficialRoles,
    roleStatuses,
    isMassEdit,
    (payload) => updateRole(payload),
    (roleId) => deleteRole(roleId),
    modal,
  );

  const [sortBy, setSortBy] = useState<string>(DEFAULT_SORT_FIELD);

  const params: Record<string, string> = {
    role: (filteredRoles.length === 0 ? seasonOfficialRoles : filteredRoles).join(","),
    limit: pageSize.toString(),
    offset: offset.toString(),
    sortBy: sortBy,
    ...(!!filteredStatus && { status: filteredStatus }),
  };

  const { data: dataSource, isFetching, refetch } = useFetchRoles(params, transformRoleResponse, seasonOfficialRoles);

  const { hasPermission } = useUserPermissions();
  const hasCreateRolePermission = hasPermission(PermissionNames.ROLES, PermissionOperations.CREATE);
  const hasReadPersonPermission = hasPermission(PermissionNames.PERSON, PermissionOperations.READ);
  const addNewOfficialAllowed = hasCreateRolePermission && hasReadPersonPermission;

  const isLoading = isFetching || isUpdating || isDeleting;

  const onChange = onTableChange<SeasonOfficialsTableRow>(setSortBy, DEFAULT_SORT_FIELD);

  return (
    <>
      {contextHolder}
      {addNewOfficialAllowed && (
        <AddPersonRolesModal
          open={isAddOfficialModalOpen}
          setOpen={setIsAddOfficialModalOpen}
          roles={seasonOfficialRoles}
          statuses={roleStatuses}
          refetch={refetch}
        />
      )}
      <div className={styles.topBar}>
        <div className={styles.topLeftContainer}>
          <Typography.Title level={4} className={styles.title}>
            {formatMessage({ id: "statistics.seasonOfficials" })}
          </Typography.Title>
        </div>
        <div className={styles.topRightContainer}>
          <div className={styles.massEditControl}>
            {formatMessage({ id: "edit.mode" })}
            <Switch value={isMassEdit} onChange={setIsMassEdit} disabled={isLoading} />
          </div>
          {addNewOfficialAllowed && (
            <Button
              onClick={() => setIsAddOfficialModalOpen(true)}
              type="primary"
              icon={<i className="fas fa-plus" />}
              disabled={isLoading}
            >
              {formatMessage({ id: "officials.add" })}
            </Button>
          )}
        </div>
      </div>
      <SeasonOfficialsFilters
        officialRoles={seasonOfficialRoles}
        setFilteredRoles={setFilteredRoles}
        setFilteredStatus={setFilteredStatus}
        roleStatuses={roleStatuses}
        disabled={isLoading}
        className={styles.filters}
      />
      <Table
        size="small"
        dataSource={dataSource}
        columns={columns}
        loading={isLoading}
        rowKey="roleId"
        components={{ body: { cell: TableCell } }}
        rootClassName={styles.tableContainer}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: pageSizeOptions,
          showQuickJumper: false,
          total: offset + (dataSource?.length ?? 0) + 1,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: () => formatMessage({ id: "pageN" }, { number: currentPage }),
        }}
        onChange={onChange}
      />
    </>
  );
};

export default SeasonOfficialsPage;
